import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const READ_BLOCK_SIZE = 0x40
const WRITE_BLOCK_SIZE = 0x10
const MEMORY_SIZE = 0x2000
const CHANNEL_SIZE = 16

type BaofengCommonSpec = {
  id: string
  vendor: string
  model: string
  summary: string
  magic: readonly number[]
  channels: number
  channelBase: number
  channelStart: number
  nameStart: number
  ackBlock: boolean
  identityResponseLength?: number
  powerLevels: readonly ['High', 'Low']
  ranges: readonly (readonly [number, number])[]
  identityOffset?: number
  identityLength?: number
  fileIds?: readonly string[]
}

export type BaofengCommonProfile = {
  protocol: 'baofeng-common'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: number
  channelMaximum: number
  nameLength: 7
  memorySize: number
  magic: readonly number[]
  channelBase: number
  channelStart: number
  nameStart: number
  ackBlock: boolean
  identityResponseLength: number
  powerLevels: readonly ['High', 'Low']
  ranges: readonly (readonly [number, number])[]
  identityOffset: number | null
  identityLength: number
  fileIds: readonly (readonly number[])[]
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

function createProfile(spec: BaofengCommonSpec): BaofengCommonProfile {
  return {
    protocol: 'baofeng-common',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      `${spec.channels} channels`,
      '7-character names',
    ],
    baudRate: 9600,
    channelMinimum: spec.channelBase,
    channelMaximum: spec.channelBase + spec.channels - 1,
    nameLength: 7,
    memorySize: MEMORY_SIZE,
    magic: spec.magic,
    channelBase: spec.channelBase,
    channelStart: spec.channelStart,
    nameStart: spec.nameStart,
    ackBlock: spec.ackBlock,
    identityResponseLength: spec.identityResponseLength ?? 8,
    powerLevels: spec.powerLevels,
    ranges: spec.ranges,
    identityOffset: spec.identityOffset ?? null,
    identityLength: spec.identityLength ?? 7,
    fileIds: (spec.fileIds ?? []).map(ascii),
  }
}

const GMRS_V1_RANGES = [
  [0x0000, 0x0df0],
  [0x0e00, 0x1800],
  [0x1ee0, 0x1ef0],
  [0x1f60, 0x1f70],
  [0x1f80, 0x1f90],
  [0x1fc0, 0x1fd0],
] as const
const GMRS_V2_RANGES = [
  [0x0000, 0x1ef0],
  [0x1f60, 0x1f70],
  [0x1f80, 0x1f90],
  [0x1fc0, 0x1fd0],
] as const
const UV6R_RANGES = [
  [0x0000, 0x1800],
  [0x1f40, 0x1f50],
  [0x1fc0, 0x1fd0],
  [0x1fe0, 0x1ff0],
] as const

const GMRS_V1_MAGIC = ascii('P_ \u0015\u0012\u0015M')
const GMRS_V2_MAGIC = ascii('P_ !\u0012\u0015M')
const UV6R_MAGIC = ascii('P\u00bb\u00ff \u0014\u0011"')
const UV5X3_MAGIC = ascii('P\r\f \u0016\u0003(')
const UV5R3_MAGIC = ascii('P\r\f \u0017\t\u0019')
const UV5X3_RANGES = [
  [0x0000, 0x0df0],
  [0x0e00, 0x1800],
  [0x1e00, 0x1e70],
  [0x1ee0, 0x1ef0],
  [0x1f60, 0x1f70],
  [0x1f80, 0x1f90],
  [0x1fa0, 0x1fb0],
  [0x1fe0, 0x2000],
] as const

export const BAOFENG_COMMON_PROFILES: readonly BaofengCommonProfile[] = [
  createProfile({
    id: 'btech-gmrs-v1',
    vendor: 'BTECH',
    model: 'GMRS-V1',
    summary: 'BTECH GMRS-V1 commercial-style GMRS handheld.',
    magic: GMRS_V1_MAGIC,
    channels: 128,
    channelBase: 0,
    channelStart: 0x0000,
    nameStart: 0x1000,
    ackBlock: true,
    powerLevels: ['High', 'Low'],
    ranges: GMRS_V1_RANGES,
    identityOffset: 0x1ef0,
    fileIds: ['US32411', 'US32416', 'US32418', 'US32412'],
  }),
  createProfile({
    id: 'btech-gmrs-v2',
    vendor: 'BTECH',
    model: 'GMRS-V2',
    summary: 'BTECH GMRS-V2 commercial-style GMRS handheld.',
    magic: GMRS_V2_MAGIC,
    channels: 200,
    channelBase: 0,
    channelStart: 0x0000,
    nameStart: 0x1000,
    ackBlock: true,
    powerLevels: ['High', 'Low'],
    ranges: GMRS_V2_RANGES,
    identityOffset: 0x1ef0,
    fileIds: ['US32419'],
  }),
  createProfile({
    id: 'btech-murs-v1',
    vendor: 'BTECH',
    model: 'MURS-V1',
    summary: 'BTECH MURS-V1 commercial-style MURS handheld.',
    magic: GMRS_V1_MAGIC,
    channels: 15,
    channelBase: 1,
    channelStart: 0x0010,
    nameStart: 0x1010,
    ackBlock: true,
    powerLevels: ['High', 'Low'],
    ranges: GMRS_V1_RANGES,
    identityOffset: 0x1ef0,
    fileIds: ['USM2402'],
  }),
  createProfile({
    id: 'btech-murs-v2',
    vendor: 'BTECH',
    model: 'MURS-V2',
    summary: 'BTECH MURS-V2 commercial-style MURS handheld.',
    magic: GMRS_V2_MAGIC,
    channels: 200,
    channelBase: 0,
    channelStart: 0x0000,
    nameStart: 0x1000,
    ackBlock: true,
    powerLevels: ['High', 'Low'],
    ranges: GMRS_V2_RANGES,
    identityOffset: 0x1ef0,
    fileIds: ['USM2403'],
  }),
  createProfile({
    id: 'baofeng-uv-6r',
    vendor: 'Baofeng',
    model: 'UV-6R',
    summary: 'Baofeng UV-6R dual-band handheld.',
    magic: UV6R_MAGIC,
    channels: 128,
    channelBase: 0,
    channelStart: 0x0000,
    nameStart: 0x1000,
    ackBlock: false,
    powerLevels: ['High', 'Low'],
    ranges: UV6R_RANGES,
    identityOffset: 0x1ff8,
    identityLength: 8,
    fileIds: [' BF230#1', ' BF230#2'],
  }),
  createProfile({
    id: 'btech-uv-5x3',
    vendor: 'BTECH',
    model: 'UV-5X3',
    summary: 'BTECH UV-5X3 tri-band handheld.',
    magic: UV5X3_MAGIC,
    channels: 128,
    channelBase: 0,
    channelStart: 0x0000,
    nameStart: 0x1000,
    ackBlock: true,
    identityResponseLength: 14,
    powerLevels: ['High', 'Low'],
    ranges: UV5X3_RANGES,
    identityOffset: 0x1ef0,
    fileIds: ['UVVG302', 'UVVG301', 'UVVG306'],
  }),
  createProfile({
    id: 'mtc-uv-5r-3',
    vendor: 'MTC',
    model: 'UV-5R-3',
    summary: 'MTC UV-5R-3 tri-band handheld.',
    magic: UV5R3_MAGIC,
    channels: 128,
    channelBase: 0,
    channelStart: 0x0000,
    nameStart: 0x1000,
    ackBlock: true,
    identityResponseLength: 14,
    powerLevels: ['High', 'Low'],
    ranges: UV5X3_RANGES,
    identityOffset: 0x1ef0,
    fileIds: ['5R31709'],
  }),
]

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'High',
    skip: false,
  }
}

function decodeName(
  image: Uint8Array,
  profile: BaofengCommonProfile,
  number: number,
): string {
  const offset =
    profile.nameStart +
    (number - profile.channelBase) * CHANNEL_SIZE
  return String.fromCharCode(
    ...image.slice(offset, offset + profile.nameLength),
  )
    .replace(/[\u0000\u00ff\ufffd]/g, '')
    .trim()
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value >= 0x0258) {
    return {
      toneMode: 'ctcss',
      ctcss: value / 10,
      dtcs: null,
      dtcsPolarity: 'N',
    }
  }
  const reversed = value > 0x69
  const index = reversed ? value - 0x6a : value - 1
  return {
    toneMode: 'dtcs',
    ctcss: null,
    dtcs: DTCS_CODES[index] ?? null,
    dtcsPolarity: reversed ? 'R' : 'N',
  }
}

function encodeTone(target: Uint8Array, channel: WebChannel): void {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(
      channel.dtcs as (typeof DTCS_CODES)[number],
    )
    if (index >= 0) {
      value = index + 1 + (channel.dtcsPolarity === 'R' ? 0x69 : 0)
    }
  }
  target[0] = value & 0xff
  target[1] = (value >> 8) & 0xff
}

export function parseBaofengCommonChannel(
  image: Uint8Array,
  profile: BaofengCommonProfile,
  number: number,
): WebChannel {
  const offset =
    profile.channelStart +
    (number - profile.channelBase) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const tone = decodeTone(raw.slice(8, 10))
  return {
    number,
    empty: false,
    name: decodeName(image, profile, number),
    rxFrequency,
    txFrequency: decodeLbcd(raw.slice(4, 8)),
    ...tone,
    mode: raw[15] & 0x40 ? 'FM' : 'NFM',
    power: profile.powerLevels[raw[14] & 0x01],
    skip: (raw[15] & 0x04) === 0,
  }
}

export function parseBaofengCommonChannels(
  image: Uint8Array,
  profile: BaofengCommonProfile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum - profile.channelMinimum + 1 },
    (_, index) =>
      parseBaofengCommonChannel(
        image,
        profile,
        profile.channelMinimum + index,
      ),
  )
}

export function applyBaofengCommonChannel(
  image: Uint8Array,
  profile: BaofengCommonProfile,
  channel: WebChannel,
): void {
  const offset =
    profile.channelStart +
    (channel.number - profile.channelBase) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  const nameOffset =
    profile.nameStart +
    (channel.number - profile.channelBase) * CHANNEL_SIZE
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0xff, nameOffset, nameOffset + profile.nameLength)
    return
  }
  if (!channel.rxFrequency) {
    throw new Error('A receive frequency is required.')
  }
  if (raw.every((value) => value === 0xff)) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const tone = new Uint8Array(2)
  encodeTone(tone, channel)
  raw.set(tone, 8)
  raw.set(tone, 10)
  raw[14] = (raw[14] & 0xfe) | (channel.power === 'Low' ? 1 : 0)
  raw[15] =
    (raw[15] & 0xb8) |
    (channel.mode === 'FM' ? 0x40 : 0) |
    (channel.skip ? 0 : 0x04)
  image.fill(0xff, nameOffset, nameOffset + profile.nameLength)
  const name = channel.name
    .toUpperCase()
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, profile.nameLength)
  for (let index = 0; index < name.length; index += 1) {
    image[nameOffset + index] = name.charCodeAt(index)
  }
}

async function enterProgrammingMode(
  profile: BaofengCommonProfile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(Uint8Array.from(profile.magic))
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error(
      `The ${profile.vendor} ${profile.model} radio refused programming mode.`,
    )
  }
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(
    profile.identityResponseLength,
    1500,
  )
  if (
    identity[0] !== 0xaa ||
    identity[identity.length - 1] !== 0xdd
  ) {
    throw new Error('The radio returned an invalid Baofeng clone identity.')
  }
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error('The radio refused the Baofeng clone session.')
  }
}

function makeReadFrame(address: number, size: number): Uint8Array {
  return Uint8Array.of(
    0x53,
    (address >> 8) & 0xff,
    address & 0xff,
    size,
  )
}

async function readBlock(
  profile: BaofengCommonProfile,
  transport: RadioTransport,
  address: number,
  size: number,
  first: boolean,
): Promise<Uint8Array> {
  await transport.write(makeReadFrame(address, size))
  if (!first && profile.ackBlock) {
    if ((await transport.readExact(1, 1500))[0] !== ACK) {
      throw new Error(
        `The radio refused block 0x${address.toString(16)}.`,
      )
    }
  }
  const header = await transport.readExact(4, 1800)
  if (
    header[0] !== 0x58 ||
    header[1] !== ((address >> 8) & 0xff) ||
    header[2] !== (address & 0xff) ||
    header[3] !== size
  ) {
    throw new Error(
      `Unexpected Baofeng reply for block 0x${address.toString(16)}.`,
    )
  }
  const data = await transport.readExact(size, 2500)
  await transport.write(Uint8Array.of(ACK))
  return data
}

function validateIdentity(
  profile: BaofengCommonProfile,
  image: Uint8Array,
): void {
  if (profile.identityOffset === null || !profile.fileIds.length) return
  const actual = image.slice(
    profile.identityOffset,
    profile.identityOffset + profile.identityLength,
  )
  if (
    !profile.fileIds.some((expected) =>
      expected.every((value, index) => actual[index] === value),
    )
  ) {
    throw new Error(
      `The connected radio does not identify as ${profile.vendor} ${profile.model}.`,
    )
  }
}

export async function readBaofengCommonRadioWithTransport(
  profile: BaofengCommonProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  const image = new Uint8Array(profile.memorySize).fill(0xff)
  const total = profile.memorySize / READ_BLOCK_SIZE
  for (
    let address = 0;
    address < profile.memorySize;
    address += READ_BLOCK_SIZE
  ) {
    image.set(
      await readBlock(
        profile,
        transport,
        address,
        READ_BLOCK_SIZE,
        address === 0,
      ),
      address,
    )
    onProgress?.({
      current: address / READ_BLOCK_SIZE + 1,
      total,
      label: `Reading ${profile.model} memory`,
    })
  }
  validateIdentity(profile, image)
  return { image, reportedModel: profile.model }
}

export async function writeBaofengCommonRadioWithTransport(
  profile: BaofengCommonProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than this radio’s memory.')
  }
  await enterProgrammingMode(profile, transport)
  const blocks = profile.ranges.reduce(
    (total, [start, end]) =>
      total + Math.ceil((end - start) / WRITE_BLOCK_SIZE),
    0,
  )
  let completed = 0
  for (const [start, end] of profile.ranges) {
    for (
      let address = start;
      address < end;
      address += WRITE_BLOCK_SIZE
    ) {
      const frame = new Uint8Array(4 + WRITE_BLOCK_SIZE)
      frame.set([
        0x58,
        (address >> 8) & 0xff,
        address & 0xff,
        WRITE_BLOCK_SIZE,
      ])
      frame.set(
        image.slice(address, address + WRITE_BLOCK_SIZE),
        4,
      )
      await transport.write(frame)
      if ((await transport.readExact(1, 1500))[0] !== ACK) {
        throw new Error(
          `The radio rejected write block 0x${address.toString(16)}.`,
        )
      }
      completed += 1
      onProgress?.({
        current: completed,
        total: blocks,
        label: `Writing ${profile.model} memory`,
      })
    }
  }
}
